import { expandAggregateDayPresence } from './mediaAggregates';
import { t } from './i18n';

export const STREAK_TYPE_DEFINITIONS = {
  music: {
    label: t('label.music'),
    eventTypes: ['spotify', 'apple_music'],
  },
  diary: {
    label: t('label.diary'),
    eventTypes: ['diary'],
  },
  checkin: {
    label: 'Check-in',
    eventTypes: ['checkin'],
  },
  route: {
    label: 'Rota / treino',
    eventTypes: ['health', 'walking', 'running', 'cycling', 'transport', 'car', 'google_maps'],
  },
  any: {
    label: 'Qualquer sinal',
    eventTypes: ['checkin', 'health', 'walking', 'running', 'cycling', 'transport', 'car', 'google_maps', 'movie', 'shows', 'concerts', 'spotify', 'apple_music', 'diary'],
  },
};

const DAY_MS = 1000 * 60 * 60 * 24;

const normalizeDate = (value) => {
  if (!value) return null;
  const date = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
};

const getSourceDate = (item) => normalizeDate(
  item?.date ||
  item?.entry_date ||
  item?.watched_at ||
  item?.event_date ||
  item?.played_at ||
  item?.route_date
);

const getRouteDate = (route) => {
  const firstPoint = (Array.isArray(route) ? route[0] : route) || {};
  return normalizeDate(firstPoint.route_date || firstPoint.date);
};

const addEvents = (target, items, eventType, getDate) => {
  (items || []).forEach((item) => {
    const date = getDate(item);
    if (!date) return;
    target.push({ date, type: eventType });
  });
};

export const buildStreakEventsFromData = (data = {}) => {
  const events = [];
  addEvents(events, data.checkins, 'checkin', getSourceDate);
  addEvents(events, data.health, 'health', getRouteDate);
  events.push(...(data.moves || []).map((route) => {
    const firstPoint = (Array.isArray(route) ? route[0] : route) || {};
    const date = getRouteDate(route);
    if (!date) return null;
    return { date, type: firstPoint.route_type || 'walking' };
  }).filter(Boolean));
  addEvents(events, data.googleMaps, 'google_maps', getRouteDate);
  addEvents(events, expandAggregateDayPresence(data.movies, 'watched_at'), 'movie', getSourceDate);
  addEvents(events, expandAggregateDayPresence(data.shows, 'watched_at'), 'shows', getSourceDate);
  addEvents(events, data.diary, 'diary', getSourceDate);
  addEvents(events, data.concerts, 'concerts', getSourceDate);
  addEvents(events, expandAggregateDayPresence(data.spotify, 'played_at'), 'spotify', getSourceDate);
  addEvents(events, expandAggregateDayPresence(data.appleMusic, 'played_at'), 'apple_music', getSourceDate);
  return events;
};

const computeSingleStreak = (days) => {
  const sorted = [...new Set(days)].sort();
  if (sorted.length === 0) {
    return { longest: 0, current: 0, totalDays: 0, startDate: null, endDate: null };
  }

  let longest = 1;
  let longestStart = sorted[0];
  let longestEnd = sorted[0];
  let currentRun = 1;
  let currentStart = sorted[0];

  for (let index = 1; index < sorted.length; index += 1) {
    const prev = new Date(sorted[index - 1]);
    const curr = new Date(sorted[index]);
    const diff = (curr - prev) / DAY_MS;

    if (diff === 1) {
      currentRun += 1;
    } else {
      currentRun = 1;
      currentStart = sorted[index];
    }

    if (currentRun > longest) {
      longest = currentRun;
      longestStart = currentStart;
      longestEnd = sorted[index];
    }
  }

  const lastDate = sorted[sorted.length - 1];
  const today = new Date().toISOString().slice(0, 10);
  const daysSinceLast = Math.floor((new Date(today) - new Date(lastDate)) / DAY_MS);
  let trailingRun = 1;

  for (let index = sorted.length - 1; index > 0; index -= 1) {
    const prev = new Date(sorted[index - 1]);
    const curr = new Date(sorted[index]);
    const diff = (curr - prev) / DAY_MS;
    if (diff === 1) trailingRun += 1;
    else break;
  }

  return {
    longest,
    current: daysSinceLast <= 1 ? trailingRun : 0,
    totalDays: sorted.length,
    startDate: longestStart,
    endDate: longestEnd,
  };
};

export const computeStreaks = (events = [], streakTypes = Object.keys(STREAK_TYPE_DEFINITIONS)) => (
  streakTypes.reduce((acc, streakType) => {
    const definition = STREAK_TYPE_DEFINITIONS[streakType];
    if (!definition) return acc;
    const days = events
      .filter((event) => definition.eventTypes.includes(event.type))
      .map((event) => event.date);
    acc[streakType] = {
      type: streakType,
      label: definition.label,
      ...computeSingleStreak(days),
    };
    return acc;
  }, {})
);
